/**
 * Admin Logs Page
 * View CRON job execution logs with filtering, sorting, and expandable error details.
 * Supports both individual job view and batch grouping view.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Layout from '../common/Layout';
import { apiService } from '../../services/api';
import { CronJobLog, CronJobStats, CronBatchSummary } from '../../types';
import '../../styles/features/admin-logs.css';

type SortColumn = 'started_at' | 'job_type' | 'status' | 'duration_seconds';
type SortDirection = 'asc' | 'desc';
type ViewMode = 'jobs' | 'batches';

const AdminLogs: React.FC = () => {
  // View mode
  const [viewMode, setViewMode] = useState<ViewMode>('batches');

  // Data state
  const [logs, setLogs] = useState<CronJobLog[]>([]);
  const [batches, setBatches] = useState<CronBatchSummary[]>([]);
  const [stats, setStats] = useState<CronJobStats | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [hoursFilter, setHoursFilter] = useState<number>(24);

  // Sort state
  const [sortColumn, setSortColumn] = useState<SortColumn>('started_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50;

  // Expanded rows/batches state
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [expandedBatches, setExpandedBatches] = useState<Set<string>>(new Set());
  const [batchDetails, setBatchDetails] = useState<Record<string, CronJobLog[]>>({});

  // Auto-refresh state
  const [autoRefresh, setAutoRefresh] = useState(false);

  // Fetch logs data
  const fetchLogs = useCallback(async () => {
    try {
      setError(null);
      const response = await apiService.getAdminLogs({
        job_category: categoryFilter || undefined,
        status: statusFilter || undefined,
        hours: hoursFilter,
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
      });
      setLogs(response.executions);
      setTotalCount(response.count);
    } catch (err: any) {
      console.error('Failed to fetch logs:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to fetch logs');
    }
  }, [categoryFilter, statusFilter, hoursFilter, currentPage]);

  // Fetch batches data
  const fetchBatches = useCallback(async () => {
    try {
      setError(null);
      const response = await apiService.getRecentBatches(hoursFilter, 50);
      setBatches(response.batches);
    } catch (err: any) {
      console.error('Failed to fetch batches:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to fetch batches');
    }
  }, [hoursFilter]);

  // Fetch stats data
  const fetchStats = useCallback(async () => {
    try {
      const response = await apiService.getAdminLogStats(hoursFilter);
      setStats(response.statistics);
    } catch (err: any) {
      console.error('Failed to fetch stats:', err);
    }
  }, [hoursFilter]);

  // Fetch batch details when expanded
  const fetchBatchDetails = useCallback(async (batchId: string) => {
    if (batchDetails[batchId]) return; // Already loaded
    try {
      const response = await apiService.getBatchDetails(batchId);
      setBatchDetails(prev => ({ ...prev, [batchId]: response.jobs }));
    } catch (err: any) {
      console.error('Failed to fetch batch details:', err);
    }
  }, [batchDetails]);

  // Initial load and filter changes
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const work = viewMode === 'jobs'
      ? Promise.all([fetchLogs(), fetchStats()])
      : Promise.all([fetchBatches(), fetchStats()]);
    work.finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [viewMode, fetchLogs, fetchBatches, fetchStats]);

  // Auto-refresh interval
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      if (viewMode === 'jobs') {
        fetchLogs();
      } else {
        fetchBatches();
      }
      fetchStats();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [autoRefresh, viewMode, fetchLogs, fetchBatches, fetchStats]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [categoryFilter, statusFilter, hoursFilter]);

  // Sort logs locally
  const sortedLogs = useMemo(() => {
    const sorted = [...logs];
    sorted.sort((a, b) => {
      let aVal: any;
      let bVal: any;

      switch (sortColumn) {
        case 'started_at':
          aVal = new Date(a.started_at).getTime();
          bVal = new Date(b.started_at).getTime();
          break;
        case 'job_type':
          aVal = a.job_type.toLowerCase();
          bVal = b.job_type.toLowerCase();
          return sortDirection === 'asc'
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
        case 'status':
          aVal = a.status;
          bVal = b.status;
          return sortDirection === 'asc'
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
        case 'duration_seconds':
          aVal = a.duration_seconds ?? 0;
          bVal = b.duration_seconds ?? 0;
          break;
        default:
          return 0;
      }

      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
    return sorted;
  }, [logs, sortColumn, sortDirection]);

  // Handle sort header click
  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  // Toggle row expansion
  const toggleRowExpand = (id: number) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  // Toggle batch expansion
  const toggleBatchExpand = (batchId: string) => {
    setExpandedBatches(prev => {
      const newSet = new Set(prev);
      if (newSet.has(batchId)) {
        newSet.delete(batchId);
      } else {
        newSet.add(batchId);
        fetchBatchDetails(batchId);
      }
      return newSet;
    });
  };

  // Format timestamp
  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  // Format duration
  const formatDuration = (seconds: number | null) => {
    if (seconds == null) return '-';
    if (seconds < 1) return `${(seconds * 1000).toFixed(0)}ms`;
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs.toFixed(0)}s`;
  };

  // Get sort indicator
  const getSortIndicator = (column: SortColumn) => {
    if (sortColumn !== column) return '⇅';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  // Get status badge class
  const getStatusClass = (status: string) => {
    switch (status) {
      case 'success':
        return 'admin-logs-status--success';
      case 'failure':
        return 'admin-logs-status--failure';
      case 'partial':
        return 'admin-logs-status--partial';
      case 'running':
        return 'admin-logs-status--running';
      default:
        return 'admin-logs-status--unknown';
    }
  };

  // Get status icon
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return '✓';
      case 'failure':
        return '✗';
      case 'partial':
        return '◐';
      case 'running':
        return '⟳';
      default:
        return '?';
    }
  };

  // Pagination
  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <Layout>
      <div className="admin-logs-container">
        <div className="admin-logs-header">
          <h1>CRON Job Logs</h1>
          <div className="admin-logs-header-controls">
            <div className="admin-logs-view-toggle">
              <button
                className={viewMode === 'batches' ? 'active' : ''}
                onClick={() => setViewMode('batches')}
              >
                Batches
              </button>
              <button
                className={viewMode === 'jobs' ? 'active' : ''}
                onClick={() => setViewMode('jobs')}
              >
                All Jobs
              </button>
            </div>
            <label className="admin-logs-auto-refresh">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
              Auto-refresh (30s)
            </label>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="admin-logs-stats">
            <div className="admin-logs-stat-card">
              <div className="admin-logs-stat-value">{stats.total_executions}</div>
              <div className="admin-logs-stat-label">Total ({hoursFilter}h)</div>
            </div>
            <div className="admin-logs-stat-card">
              <div className="admin-logs-stat-value admin-logs-stat-value--success">
                {stats.success_rate_pct.toFixed(1)}%
              </div>
              <div className="admin-logs-stat-label">Success Rate</div>
            </div>
            <div className="admin-logs-stat-card">
              <div className="admin-logs-stat-value admin-logs-stat-value--failure">
                {stats.failure_count}
              </div>
              <div className="admin-logs-stat-label">Failures</div>
            </div>
            <div className="admin-logs-stat-card">
              <div className="admin-logs-stat-value">
                {stats.avg_duration_seconds != null
                  ? formatDuration(stats.avg_duration_seconds)
                  : '-'}
              </div>
              <div className="admin-logs-stat-label">Avg Duration</div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="admin-logs-filters">
          {viewMode === 'jobs' && (
            <>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="admin-logs-filter-select"
              >
                <option value="">All Categories</option>
                <option value="bronze">Bronze</option>
                <option value="silver">Silver</option>
                <option value="gold">Gold</option>
                <option value="system">System</option>
              </select>

              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="admin-logs-filter-select"
              >
                <option value="">All Status</option>
                <option value="success">Success</option>
                <option value="failure">Failure</option>
                <option value="partial">Partial</option>
                <option value="running">Running</option>
              </select>
            </>
          )}

          <select
            value={hoursFilter}
            onChange={(e) => setHoursFilter(Number(e.target.value))}
            className="admin-logs-filter-select"
          >
            <option value={6}>Last 6 hours</option>
            <option value={12}>Last 12 hours</option>
            <option value={24}>Last 24 hours</option>
            <option value={48}>Last 48 hours</option>
            <option value={168}>Last 7 days</option>
          </select>

          <button
            onClick={() => {
              if (viewMode === 'jobs') {
                fetchLogs();
              } else {
                fetchBatches();
              }
              fetchStats();
            }}
            className="admin-logs-refresh-btn"
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="admin-logs-error">
            {error}
          </div>
        )}

        {/* Batches View */}
        {viewMode === 'batches' && (
          <div className="admin-logs-batches">
            {batches.length === 0 && !loading && (
              <div className="admin-logs-empty">
                No batches found for the selected time period
              </div>
            )}
            {batches.map((batch) => (
              <div key={batch.batch_id} className="admin-logs-batch-card">
                <div
                  className="admin-logs-batch-header"
                  onClick={() => toggleBatchExpand(batch.batch_id)}
                >
                  <div className="admin-logs-batch-info">
                    <span className="admin-logs-expand-icon">
                      {expandedBatches.has(batch.batch_id) ? '▼' : '▶'}
                    </span>
                    <div className="admin-logs-batch-name">
                      {batch.batch_name || batch.batch_id}
                    </div>
                    <div className="admin-logs-batch-time">
                      {formatTime(batch.started_at)} - {formatDate(batch.started_at)}
                    </div>
                  </div>
                  <div className="admin-logs-batch-stats">
                    <span className={`admin-logs-status ${getStatusClass(batch.status)}`}>
                      {getStatusIcon(batch.status)}
                    </span>
                    <span className="admin-logs-batch-job-count">
                      {batch.total_jobs} jobs
                    </span>
                    <span className="admin-logs-batch-success">
                      {batch.success_count} ✓
                    </span>
                    {batch.failure_count > 0 && (
                      <span className="admin-logs-batch-failure">
                        {batch.failure_count} ✗
                      </span>
                    )}
                    <span className="admin-logs-batch-duration">
                      {formatDuration(batch.duration_seconds)}
                    </span>
                  </div>
                </div>
                <div className="admin-logs-batch-categories">
                  {batch.categories.map((cat) => (
                    <span key={cat} className={`admin-logs-category admin-logs-category--${cat}`}>
                      {cat}
                    </span>
                  ))}
                </div>

                {/* Expanded batch details */}
                {expandedBatches.has(batch.batch_id) && (
                  <div className="admin-logs-batch-details">
                    {batchDetails[batch.batch_id] ? (
                      <table className="admin-logs-table admin-logs-table--compact">
                        <thead>
                          <tr>
                            <th className="admin-logs-th">Job Type</th>
                            <th className="admin-logs-th">Category</th>
                            <th className="admin-logs-th">Status</th>
                            <th className="admin-logs-th admin-logs-th--numeric">Duration</th>
                            <th className="admin-logs-th admin-logs-th--numeric">Records</th>
                          </tr>
                        </thead>
                        <tbody>
                          {batchDetails[batch.batch_id].map((job) => (
                            <tr key={job.id} className="admin-logs-row">
                              <td className="admin-logs-td admin-logs-job-type">
                                {job.job_type}
                                {job.parameters?.symbol && (
                                  <span className="admin-logs-symbol">
                                    ({job.parameters.symbol})
                                  </span>
                                )}
                              </td>
                              <td className="admin-logs-td">
                                <span className={`admin-logs-category admin-logs-category--${job.job_category}`}>
                                  {job.job_category}
                                </span>
                              </td>
                              <td className="admin-logs-td">
                                <span className={`admin-logs-status ${getStatusClass(job.status)}`}>
                                  {getStatusIcon(job.status)}
                                </span>
                              </td>
                              <td className="admin-logs-td admin-logs-td--numeric">
                                {formatDuration(job.duration_seconds)}
                              </td>
                              <td className="admin-logs-td admin-logs-td--numeric">
                                {job.records_processed ?? '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div className="admin-logs-loading">Loading batch details...</div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Jobs View (original table) */}
        {viewMode === 'jobs' && (
          <>
            <div className="admin-logs-table-container">
              <table className="admin-logs-table">
                <thead>
                  <tr>
                    <th
                      className="admin-logs-th admin-logs-th--sortable"
                      onClick={() => handleSort('started_at')}
                    >
                      Started {getSortIndicator('started_at')}
                    </th>
                    <th
                      className="admin-logs-th admin-logs-th--sortable"
                      onClick={() => handleSort('job_type')}
                    >
                      Job Type {getSortIndicator('job_type')}
                    </th>
                    <th className="admin-logs-th">Category</th>
                    <th
                      className="admin-logs-th admin-logs-th--sortable"
                      onClick={() => handleSort('status')}
                    >
                      Status {getSortIndicator('status')}
                    </th>
                    <th
                      className="admin-logs-th admin-logs-th--sortable admin-logs-th--numeric"
                      onClick={() => handleSort('duration_seconds')}
                    >
                      Duration {getSortIndicator('duration_seconds')}
                    </th>
                    <th className="admin-logs-th admin-logs-th--numeric">Records</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedLogs.length === 0 && !loading && (
                    <tr>
                      <td colSpan={6} className="admin-logs-empty">
                        No logs found for the selected filters
                      </td>
                    </tr>
                  )}
                  {sortedLogs.map((log) => (
                    <React.Fragment key={log.id}>
                      <tr
                        className={`admin-logs-row ${expandedRows.has(log.id) ? 'admin-logs-row--expanded' : ''} ${log.error_message ? 'admin-logs-row--clickable' : ''}`}
                        onClick={() => log.error_message && toggleRowExpand(log.id)}
                      >
                        <td className="admin-logs-td">
                          <div className="admin-logs-time">
                            <span className="admin-logs-time-value">{formatTime(log.started_at)}</span>
                            <span className="admin-logs-date">{formatDate(log.started_at)}</span>
                          </div>
                        </td>
                        <td className="admin-logs-td admin-logs-job-type">
                          {log.error_message && (
                            <span className="admin-logs-expand-icon">
                              {expandedRows.has(log.id) ? '▼' : '▶'}
                            </span>
                          )}
                          {log.job_type}
                          {log.batch_id && (
                            <span className="admin-logs-batch-badge" title={log.batch_id}>
                              batch
                            </span>
                          )}
                        </td>
                        <td className="admin-logs-td">
                          <span className={`admin-logs-category admin-logs-category--${log.job_category}`}>
                            {log.job_category}
                          </span>
                        </td>
                        <td className="admin-logs-td">
                          <span className={`admin-logs-status ${getStatusClass(log.status)}`}>
                            {getStatusIcon(log.status)}
                          </span>
                        </td>
                        <td className="admin-logs-td admin-logs-td--numeric">
                          {formatDuration(log.duration_seconds)}
                        </td>
                        <td className="admin-logs-td admin-logs-td--numeric">
                          {log.records_processed != null ? (
                            <>
                              {log.records_processed}
                              {log.records_failed != null && log.records_failed > 0 && (
                                <span className="admin-logs-failed-count">
                                  ({log.records_failed} failed)
                                </span>
                              )}
                            </>
                          ) : (
                            '-'
                          )}
                        </td>
                      </tr>
                      {/* Expanded Error Details */}
                      {expandedRows.has(log.id) && log.error_message && (
                        <tr className="admin-logs-detail-row">
                          <td colSpan={6} className="admin-logs-detail-cell">
                            <div className="admin-logs-error-details">
                              {log.error_type && (
                                <div className="admin-logs-error-type">
                                  <strong>Error Type:</strong> {log.error_type}
                                </div>
                              )}
                              <div className="admin-logs-error-message">
                                <strong>Message:</strong> {log.error_message}
                              </div>
                              {log.stack_trace && (
                                <div className="admin-logs-stack-trace">
                                  <strong>Stack Trace:</strong>
                                  <pre>{log.stack_trace}</pre>
                                </div>
                              )}
                              {log.parameters && Object.keys(log.parameters).length > 0 && (
                                <div className="admin-logs-parameters">
                                  <strong>Parameters:</strong>
                                  <pre>{JSON.stringify(log.parameters, null, 2)}</pre>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="admin-logs-pagination">
                <button
                  onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="admin-logs-page-btn"
                >
                  &lt; Prev
                </button>
                <span className="admin-logs-page-info">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="admin-logs-page-btn"
                >
                  Next &gt;
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default AdminLogs;
